/**
 * The leaderboards.
 *
 * Two boards, because cash and tournaments are not the same game and one table
 * that mixed them would rank nobody meaningfully.
 *
 * The column that decides the order is the one on the right, in gold, with the
 * name of the metric above it — BB/100 for cash, ROI for tournaments. Everything
 * else is context for it. A leaderboard whose sort order the reader has to guess
 * at is a table, not a leaderboard.
 *
 * Players below the qualifying threshold stay on the board, under a line, with
 * how far they have to go. Hiding them would remove the one thing that brings a
 * new player back.
 */

import { useCallback, useEffect, useState } from 'react';
import { Avatar } from './avatar.tsx';
import { PageTitle, SiteHeader } from './Shell.tsx';
import { api } from '../server.ts';

type BoardKind = 'cash' | 'sng';
type Window = '7d' | '30d' | 'all';

interface CashRow {
  rank: number | null;
  playerId: string;
  name: string;
  hands: number;
  netBB: number;
  bb100: number;
  biggestPotBB: number;
  handsToRank: number;
}

interface SngRow {
  rank: number | null;
  playerId: string;
  name: string;
  played: number;
  wins: number;
  cashes: number;
  profit: number;
  roi: number;
  gamesToRank: number;
}

interface Response {
  board: BoardKind;
  window: Window;
  rows: (CashRow | SngRow)[];
  you: CashRow | SngRow | null;
  minimum: number;
}

const WINDOW_LABEL: Record<Window, string> = {
  '7d': '۷ روز',
  '30d': '۳۰ روز',
  all: 'همیشه',
};

export function Leaderboard({ session }: { session: string | null }) {
  const [board, setBoard] = useState<BoardKind>('cash');
  const [window, setWindow] = useState<Window>('30d');
  const [data, setData] = useState<Response | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await fetch(api(`/api/leaderboard?board=${board}&window=${window}`), {
        headers: session === null ? {} : { authorization: `Bearer ${session}` },
      });
      if (!response.ok) {
        setError(`خطا ${response.status}`);
        return;
      }
      setData((await response.json()) as Response);
      setError(null);
    } catch {
      setError('سرور در دسترس نیست.');
    }
  }, [board, window, session]);

  useEffect(() => {
    void load();
  }, [load]);

  const ranked = (data?.rows ?? []).filter((row) => row.rank !== null);
  const unranked = (data?.rows ?? []).filter((row) => row.rank === null);
  // Listed anywhere on the page, not just above the cut. Somebody short of
  // the qualifying hands appears in the unranked group, and checking only the
  // ranked list pinned a second copy of them at the bottom — the same row,
  // twice, a few centimetres apart.
  const youAreListed =
    data?.you != null &&
    [...ranked, ...unranked].some((row) => row.playerId === data.you?.playerId);

  return (
    <>
      <SiteHeader here="/leaderboard" />
      <div className="page leaderboard">
        {/* A band rather than a heading. A ranking is the room's own scoreboard
            and it should arrive looking like one — the name of the board, what
            it measures, and how much of it there is to be ranked against. */}
        <div className="board-hero">
          <div className="board-hero-copy">
            <span className="board-badge lat-ui">Iran Poker Tour</span>
            <h1>Ranking</h1>
            <p>
              {board === 'cash'
                ? 'ترتیب بر اساس BB/100 — چیپ برده به‌ازای هر صد دست، بر حسب بیگ بلایند. مستقل از سطح میز، پس یک ساعت روی میز بزرگ جای هزار دست روی میز کوچک را نمی‌گیرد.'
                : 'ترتیب بر اساس ROI — سود به‌نسبت کل بای-این‌ها. تنها عددی که یک بازیکن تورنمنت با آن سنجیده می‌شود.'}
            </p>
          </div>
          <div className="board-hero-figures">
            <div className="board-figure">
              <span className="lat-ui">Ranked</span>
              <b className="num lat-ui">{ranked.length}</b>
            </div>
            <div className="board-figure">
              <span className="lat-ui">Qualifying</span>
              <b className="num lat-ui">{data?.minimum ?? '—'}</b>
            </div>
          </div>
        </div>

      <div className="board-switch" role="tablist">
        <button role="tab" aria-selected={board === 'cash'} className={board === 'cash' ? 'on' : ''} onClick={() => setBoard('cash')}>
          Cash Games
        </button>
        <button role="tab" aria-selected={board === 'sng'} className={board === 'sng' ? 'on' : ''} onClick={() => setBoard('sng')}>
          Sit &amp; Go
        </button>
        <span className="spacer" />
        {(['7d', '30d', 'all'] as Window[]).map((option) => (
          <button
            key={option}
            className={`window${window === option ? ' on' : ''}`}
            onClick={() => setWindow(option)}
          >
            {WINDOW_LABEL[option]}
          </button>
        ))}
      </div>

      {error !== null && <div className="error">{error}</div>}
      {data !== null && data.you !== null && (
        <div className="standing">
          <Avatar name={data.you.name} />
          <div className="standing-who">
            <span className="standing-label lat-ui">Your Standing</span>
            <div className="standing-name">{data.you.name}</div>
            <div className="standing-sub">
              {data.you.rank === null
                ? `هنوز رتبه ندارید — ${data.minimum} ${board === 'cash' ? 'دست' : 'بازی'} لازم است`
                : `رتبه‌ی ${data.you.rank} از ${ranked.length} بازیکن رتبه‌دار`}
            </div>
          </div>
          <div className="standing-figures">
            <Figure
              label={board === 'cash' ? 'BB/100' : 'ROI'}
              value={signed(board === 'cash' ? (data.you as CashRow).bb100 : (data.you as SngRow).roi)}
              tone={signClass(board === 'cash' ? (data.you as CashRow).bb100 : (data.you as SngRow).roi)}
            />
            <Figure
              label={board === 'cash' ? 'Hands' : 'Games'}
              value={String(board === 'cash' ? (data.you as CashRow).hands : (data.you as SngRow).played)}
            />
            <Figure label="Rank" value={data.you.rank === null ? '—' : `#${data.you.rank}`} />
          </div>
          <a className="btn-plain small" href="/profile">
            View Profile
          </a>
        </div>
      )}
      {/* Nobody has qualified yet. Said where the podium would be, because a
          gap where the top three should stand reads as something failing to
          load rather than as a board waiting to be filled. */}
      {data !== null && ranked.length === 0 && (
        <div className="board-empty">
          <span className="board-empty-mark" aria-hidden="true">—</span>
          <h2>هنوز کسی روی سکو نیست</h2>
          <p>
            برای رتبه گرفتن {data.minimum} {board === 'cash' ? 'دست' : 'بازی'} لازم است.
            بازیکنانی که هنوز به حد نصاب نرسیده‌اند پایین‌تر فهرست شده‌اند.
          </p>
        </div>
      )}
      {data !== null && ranked.length >= 3 && (
        <div className="board-podium">
          {podiumOrder(ranked.slice(0, 3)).map((row) => (
            <div key={row.playerId} className={`board-slot place${row.rank}`}>
              <span className="board-slot-title lat-ui">
                {row.rank === 1 ? 'Champion' : row.rank === 2 ? 'Runner-Up' : 'Third'}
              </span>
              <Avatar name={row.name} />
              <div className="board-slot-name">{row.name}</div>
              <div className="board-slot-figure num lat-ui">
                {signed(board === 'cash' ? (row as CashRow).bb100 : (row as SngRow).roi)}
                {board === 'sng' && '%'}
              </div>
            </div>
          ))}
        </div>
      )}
      {data === null ? (
        <p className="sub">در حال خواندن…</p>
      ) : (
        <>
          <div className="lb-board">
            <Header board={board} />

            {ranked.map((row) => (
              <Row key={row.playerId} board={board} row={row} you={row.playerId === data.you?.playerId} />
            ))}

            {unranked.length > 0 && (
              <>
                <div className="cut lat-ui">
                  Not yet ranked — {data.minimum} {board === 'cash' ? 'hands' : 'games'} needed
                </div>
                {unranked.map((row) => (
                  <Row key={row.playerId} board={board} row={row} you={row.playerId === data.you?.playerId} />
                ))}
              </>
            )}
          </div>

          {/* Pinned, so a player never has to look for themselves. */}
          {data.you !== null && !youAreListed && (
            <div className="lb-board pinned">
              <div className="cut lat-ui">You</div>
              <Row board={board} row={data.you} you />
            </div>
          )}
        </>
      )}
      </div>
    </>
  );
}

function Header({ board }: { board: BoardKind }) {
  return (
    // No lat-ui on the row itself. That class carries direction: ltr for the
    // sake of the Latin words inside it, and on a grid the direction decides
    // which end the first column starts at — so the whole header laid itself
    // out backwards from the rows underneath, with the rank label at the
    // opposite margin from the ranks. The font is applied in the stylesheet
    // instead, where it cannot take the layout with it.
    <div className="lb-row head">
      <span className="lb-rank">#</span>
      <span className="lb-name">Player</span>
      {board === 'cash' ? (
        <>
          <span className="lb-n">Hands</span>
          <span className="lb-n">Net BB</span>
          <span className="lb-n">Best Pot</span>
          <span className="lb-key">BB/100</span>
        </>
      ) : (
        <>
          <span className="lb-n">Games</span>
          <span className="lb-n">Wins</span>
          <span className="lb-n">Cashes</span>
          <span className="lb-key">ROI</span>
        </>
      )}
    </div>
  );
}

/** Second to one side of first, third to the other. A podium, not a list. */
function podiumOrder<T extends { rank: number | null }>(rows: readonly T[]): T[] {
  const byRank = [...rows].sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));
  return [byRank[1], byRank[0], byRank[2]].filter((row): row is T => row !== undefined);
}

/** One labelled number, in the standing card. */
function Figure({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="standing-figure">
      <span className="lat-ui">{label}</span>
      <bdi className={`num lat-ui ${tone ?? ''}`}>{value}</bdi>
    </div>
  );
}

/** The top three get the metal, and everybody else gets a number. */
function Medal({ place }: { place: number }) {
  return (
    <span className={`lb-medal place${place}`} aria-label={`#${place}`}>
      {place}
    </span>
  );
}

function Row({ board, row, you }: { board: BoardKind; row: CashRow | SngRow; you: boolean }) {
  const key = board === 'cash' ? (row as CashRow).bb100 : (row as SngRow).roi;
  return (
    <div className={`lb-row${you ? ' you' : ''}${row.rank !== null && row.rank <= 3 ? ` top top${row.rank}` : ''}`}>
      <span className="lb-rank lat-ui">
        {row.rank !== null && row.rank <= 3 ? <Medal place={row.rank} /> : (row.rank ?? '—')}
      </span>
      <span className="lb-name">
        <Avatar name={row.name} />
        <span className="lb-who">{row.name}</span>
      </span>
      {board === 'cash' ? (
        <>
          <span className="lb-n lat-ui">{(row as CashRow).hands.toLocaleString('en-US')}</span>
          <span className={`lb-n lat-ui ${signClass((row as CashRow).netBB)}`}>
            {signed((row as CashRow).netBB)}
          </span>
          <span className="lb-n lat-ui">{(row as CashRow).biggestPotBB.toFixed(1)}</span>
        </>
      ) : (
        <>
          <span className="lb-n lat-ui">{(row as SngRow).played}</span>
          <span className="lb-n lat-ui">{(row as SngRow).wins}</span>
          <span className="lb-n lat-ui">{(row as SngRow).cashes}</span>
        </>
      )}
      <span className={`lb-key lat-ui ${signClass(key)}`}>
        {signed(key)}
        {board === 'sng' && '%'}
      </span>
    </div>
  );
}

function signed(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return `${rounded > 0 ? '+' : rounded < 0 ? '−' : ''}${Math.abs(rounded).toLocaleString('en-US', {
    maximumFractionDigits: 2,
  })}`;
}

function signClass(value: number): string {
  return value > 0 ? 'up' : value < 0 ? 'down' : '';
}
